import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AmountSource } from "./amounts";

// Confidence at or above this is "trusted"; below it the UI flags the
// field for a human look. Mirrors interchange-web's threshold, and the
// server sets edited fields to 1.0 so a correction clears the flag.
export const CONFIDENCE_THRESHOLD = 0.7;

// Mirrors MOVE_TYPES in packages/pre-settlement/src/db/schema/load.ts.
// The PATCH endpoint rejects anything outside this set with a 400.
export const MOVE_TYPES = ["EMPTY_IN", "EMPTY_OUT", "FULL_IN", "FULL_OUT"] as const;
export type MoveType = (typeof MOVE_TYPES)[number];

// The query key every loads-list read shares. Capture and PATCH both
// invalidate it so the list reflects a new/priced load on return.
export const LOADS_KEY = "pre-settlement-loads";

// One row from GET /api/pre-settlement/loads — the list projection the
// server selects, not the whole load row.
export interface LoadListRow {
  id: number;
  loadDate: string;
  amount: string | null;
  containerNo: string | null;
  carrierCode: string | null;
  carrierName: string | null;
  moveType: string | null;
  eirNumber: string | null;
  grossWeightLb: string | null;
  parserConfidence: number | null;
  createdAt: string;
}

// Per-field provenance (mirrors the server's field_provenance jsonb),
// keyed in the PARSER's namespace — e.g. "carrier" covers both
// carrierCode and carrierName.
export interface FieldProvenanceEntry {
  source: string;
  editedBy?: string;
  editedAt?: string;
}

// The full pre_settlement.load row, as GET :id and PATCH :id return it.
// numeric columns arrive as strings (Drizzle's numeric representation).
export interface LoadRow {
  id: number;
  organizationId: string;
  loadDate: string;
  amount: string | null;
  eirNumber: string | null;
  moveType: string | null;
  carrierName: string | null;
  carrierCode: string | null;
  containerNo: string | null;
  containerSizeType: string | null;
  chassisNo: string | null;
  grossWeightLb: string | null;
  scaleWeightLb: string | null;
  pin: string | null;
  eirDatetime: string | null;
  sourceDocumentId: number | null;
  sourceImageUrl: string | null;
  notes: string | null;
  parserConfidence: number | null;
  fieldConfidences: Record<string, number> | null;
  fieldRawTexts: Record<string, string> | null;
  fieldProvenance: Record<string, FieldProvenanceEntry> | null;
  createdAt: string;
  updatedAt: string;
}

// Carries the HTTP status alongside the server's message so callers can
// tell "you typed something invalid" (400) from "you're signed out"
// (401) without string-matching the message.
export class ApiError extends Error {
  readonly status: number;
  // The whole error body, so a caller that needs more than the message can
  // read it without a second parse. A 402 carries `reason` here, which is
  // what decides the paywall's copy.
  readonly body: Record<string, unknown> | null;

  constructor(message: string, status: number, body: Record<string, unknown> | null = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

// Did this failure mean "you need to subscribe"? The server answers 402 for
// exactly one situation, so every screen can ask the same question of any
// error it catches instead of matching on strings.
export function isPaymentRequired(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 402;
}

// The reason behind a 402, when there is one. Falls back to "none" so a
// caller always has a branch to take.
export function paymentReason(err: unknown): EntitlementReason {
  if (!isPaymentRequired(err)) return "none";
  const reason = err.body?.reason;
  return typeof reason === "string" ? (reason as EntitlementReason) : "none";
}

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, { credentials: "include", ...init });
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* non-json */ }
    const msg = (body && typeof body === "object" && "error" in body)
      ? String((body as { error: unknown }).error)
      : `HTTP ${res.status}`;
    throw new ApiError(
      msg,
      res.status,
      body && typeof body === "object" ? (body as Record<string, unknown>) : null,
    );
  }
  return res.json() as Promise<T>;
}

// One place that turns a thrown error into driver-facing copy. A 401
// means the session lapsed — telling them "unauthorized" would be
// accurate and useless, so it gets its own sentence.
export function describeError(err: unknown): string {
  if (err instanceof ApiError && err.status === 401) {
    return "Your session has expired. Sign in again.";
  }
  if (isPaymentRequired(err)) {
    return paymentReason(err) === "trial_expired"
      ? "Your free trial has ended."
      : "Your subscription isn't active.";
  }
  return err instanceof Error ? err.message : "Something went wrong";
}

export function useLoadsList() {
  return useQuery({
    queryKey: [LOADS_KEY],
    queryFn: () => jsonFetch<{ loads: LoadListRow[] }>("/api/pre-settlement/loads"),
  });
}

// The same list projection narrowed to a business-day range (inclusive
// both ends), which is what the report reads — one fetch covers a whole
// settlement week and both the Daily and Weekly views derive from it.
// The key nests under LOADS_KEY so capture/PATCH invalidation reaches it
// without either mutation having to know the report exists.
export function useLoadsRange(from: string, to: string) {
  return useQuery({
    queryKey: [LOADS_KEY, "range", from, to],
    queryFn: () =>
      jsonFetch<{ loads: LoadListRow[] }>(
        `/api/pre-settlement/loads?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
  });
}

export function useLoad(id: number | null) {
  return useQuery({
    queryKey: [LOADS_KEY, id],
    queryFn: () => jsonFetch<{ load: LoadRow }>(`/api/pre-settlement/loads/${id}`),
    enabled: id !== null,
  });
}

export const CAPTURE_URL = "/api/pre-settlement/loads/capture";

// Capture: POST a photographed EIR as multipart (field "file"). The
// server prepares the image and reads it with a vision model
// synchronously — expect ~1-3s — and returns the created load prefilled
// from that reading. The prefill INCLUDES `amount`, the handwritten pay,
// which is the reason slice 8 swapped extractors: Tesseract could not
// read a marker scrawl and this can. It is still a prefill — when the
// ticket carries no handwritten figure `amount` is null and the driver
// types it, and either way they confirm it.
//
// A ticket the model cannot read comes back 422 with a driver-facing
// message, NOT a 5xx: one bad photo fails its own row and the rest of a
// bulk upload still commits.
//
// Exported as a plain function as well as a hook: bulk capture drives
// many of these through its own bounded-concurrency runner, which a
// per-file mutation hook can't express.
export function captureLoad(file: File): Promise<{ load: LoadRow }> {
  const fd = new FormData();
  fd.append("file", file);
  return jsonFetch(CAPTURE_URL, { method: "POST", body: fd });
}

export function useCaptureLoad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: captureLoad,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [LOADS_KEY] });
    },
  });
}

// --- Batch amount confirm ------------------------------------------------

export interface BatchAmountUpdate {
  id: number;
  amount: number;
  // Which rule produced this amount (see lib/amounts.ts). Sent alongside
  // the value so field_provenance.amount records that a flat rate or a
  // round-trip split priced this load, not a person reading a ticket.
  amountSource?: AmountSource;
}

// Confirm All: one PATCH per row the driver filled or edited. Sent
// SEQUENTIALLY — unlike capture these are cheap metadata writes, and
// doing them in order means a mid-batch failure leaves an obvious
// prefix committed rather than an arbitrary scatter.
export function useConfirmBatchAmounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updates: BatchAmountUpdate[]): Promise<number> => {
      for (const { id, amount, amountSource } of updates) {
        await jsonFetch(`/api/pre-settlement/loads/${id}`, {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(
            amountSource ? { amount, amountSource } : { amount },
          ),
        });
      }
      return updates.length;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [LOADS_KEY] });
    },
  });
}

// Field -> new value. The server validates every key against its
// EDITABLE_FIELDS allowlist and rejects the whole request 400 if any
// key is unknown or any value is the wrong type.
export type LoadPatchPayload = Record<string, string | number | null>;

export function usePatchLoad(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: LoadPatchPayload): Promise<{ load: LoadRow }> =>
      jsonFetch(`/api/pre-settlement/loads/${id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      // Both the list projection and this load's detail are now stale.
      void qc.invalidateQueries({ queryKey: [LOADS_KEY] });
    },
  });
}

// --- Send to Dispatch ----------------------------------------------------

// The org's saved Send-to-Dispatch defaults. Both nullable: a driver who
// has never sent has neither yet, and the send form asks for them then.
export interface ReportSettings {
  dispatchEmail: string | null;
  tractor: string | null;
  // The batch flat rate in INTEGER CENTS; null means the driver has never
  // set one, in which case no default is applied at all.
  defaultRateCents: number | null;
  // The weekly auto-send. When on, the server mails the week that just
  // closed to `dispatchEmail` on `weeklyAutosendDow` at
  // `weeklyAutosendHour` terminal-local — no browser involved, so this is
  // a stored preference and not a client job.
  weeklyAutosendEnabled: boolean;
  weeklyAutosendHour: number;
  // JS getUTCDay() convention: 0 = Sunday, 1 = Monday, … 6 = Saturday.
  // Defaults to 1 (Monday) — see the column comment server-side.
  weeklyAutosendDow: number;
}

export const SETTINGS_KEY = "pre-settlement-settings";

// `enabled` is a parameter because the report only needs these when the
// driver opens the send form — a screen they may never open shouldn't
// spend a request on it.
export function useSettings(enabled = true) {
  return useQuery({
    queryKey: [SETTINGS_KEY],
    queryFn: () => jsonFetch<{ settings: ReportSettings }>("/api/pre-settlement/settings"),
    enabled,
  });
}

// Save any subset of the org's settings. An omitted key is left alone;
// an explicit null clears it. Batch review uses this to remember the
// default rate on Confirm All.
export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<ReportSettings>): Promise<{ settings: ReportSettings }> =>
      jsonFetch("/api/pre-settlement/settings", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [SETTINGS_KEY] });
    },
  });
}

// What the client is allowed to say about a report: which window, and
// who to mail it to. Never the loads and never the total — the server
// re-derives both from the database, so a stale or tampered client
// cannot change what dispatch is told.
export interface SendReportPayload {
  scope: "day" | "week";
  date: string;
  dispatchEmail: string;
  tractor?: string | null;
}

export interface SendReportResult {
  sent: true;
  to: string;
  // False on a server with no RESEND_API_KEY: the report was composed
  // and logged but never put on the wire. The UI says so rather than
  // claiming a delivery that didn't happen.
  delivered: boolean;
  transport: "resend" | "log";
  scope: "day" | "week";
  from: string;
  through: string;
  settlesTuesday: string | null;
  subject: string;
  count: number;
  // The server's own total, formatted — what the email actually said.
  total: string;
  needAmountCount: number;
}

export function useSendReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendReportPayload): Promise<SendReportResult> =>
      jsonFetch("/api/pre-settlement/reports/send", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      // The send remembers the address used as the org's new default.
      void qc.invalidateQueries({ queryKey: [SETTINGS_KEY] });
    },
  });
}

// URL for a load's captured ticket image, served by
// GET /api/pre-settlement/loads/:id/file.
export function loadFileUrl(id: number): string {
  return `/api/pre-settlement/loads/${id}/file`;
}

// --- Billing -------------------------------------------------------------
//
// Everything about money happens on Stripe's pages. This client asks the
// server for a URL and sends the browser there; it never renders a card
// field and never handles card data, which is the point.

// Mirrors EntitlementReason in @skip/core/billing. It decides the copy, so
// it is a union rather than a string: a reason the UI has no sentence for
// should be a type error, not a blank banner.
export type EntitlementReason =
  // Comped: entitled without paying and with no Stripe subscription at all.
  // Allowed like "active", but shown differently — a comped org has no
  // billing to manage and no trial to count down.
  | "comp"
  | "trialing"
  | "active"
  | "trial_expired"
  | "canceled"
  | "none";

export interface BillingStatus {
  // The raw subscription status, for display; `reason` is what to branch on.
  status: string;
  reason: EntitlementReason;
  allowed: boolean;
  // Whole days remaining, only while trialing.
  daysLeft: number | null;
  trialEndsAt: string;
  currentPeriodEnd: string | null;
  // True once the org has a Stripe customer — i.e. there is a billing
  // account to manage.
  manageable: boolean;
  // False on a server with no Stripe keys. The Subscribe button would have
  // nowhere to send them, so it isn't offered.
  configured: boolean;
}

export const BILLING_KEY = "pre-settlement-billing";

export function useBillingStatus() {
  return useQuery({
    queryKey: [BILLING_KEY],
    queryFn: () => jsonFetch<BillingStatus>("/api/billing/status"),
    // A trial can lapse while the app is open, and a subscription lands via
    // a webhook the client never sees. Re-checking on focus is how the
    // banner stops being wrong without polling.
    refetchOnWindowFocus: true,
  });
}

// Ask the server to open a Stripe-hosted Checkout Session, then leave for
// Stripe's page. The redirect is the mutation's whole job — there is no
// success state to render here, because the next thing the driver sees is
// stripe.com.
export function useStartCheckout() {
  return useMutation({
    mutationFn: async (): Promise<{ url: string }> => {
      const res = await jsonFetch<{ url: string }>("/api/billing/checkout", {
        method: "POST",
      });
      window.location.assign(res.url);
      return res;
    },
  });
}

// The Billing Portal: update the card, download invoices, cancel. Also
// Stripe-hosted, for the same reason.
//
// The server answers 200 with `url: null` when the org has no live Stripe
// customer — never subscribed, or holding an id Stripe no longer knows. That
// is a re-subscribe state, not an error, so the mutation refreshes the
// billing status (which now reports `manageable: false`, hiding this link)
// rather than navigating to nowhere.
export function useOpenBillingPortal() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<{ url: string | null; checkoutRequired?: boolean }> => {
      const res = await jsonFetch<{ url: string | null; checkoutRequired?: boolean }>(
        "/api/billing/portal",
        { method: "POST" },
      );
      if (res.url) window.location.assign(res.url);
      return res;
    },
    onSuccess: (res) => {
      if (!res.url) void qc.invalidateQueries({ queryKey: [BILLING_KEY] });
    },
  });
}
